"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { fetchLinkPreview } from "@/services/api/link-preview-api";
import { fetchNftLink } from "@/services/api/nft-link-api";
import { fromNftLink, mergeOpenGraphFallback, needsOpenGraphFallback, type MarketplacePreviewMode, type MarketplacePreviewState } from "./common";
interface UseMarketplacePreviewStateParams {
  readonly href: string;
  readonly mode?: MarketplacePreviewMode | undefined;
}
const MARKETPLACE_PREVIEW_STALE_TIME_MS = 5 * 60 * 1000;
const toError = (value: unknown, fallbackMessage: string): Error => value instanceof Error ? value : new Error(fallbackMessage);
const toNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};
export const useMarketplacePreviewState = t0 => {
  const $ = _c(28);
  const {
    href,
    mode: t1
  } = t0;
  const mode = t1 === undefined ? "default" : t1;
  let t2;
  if ($[0] !== href) {
    t2 = href.trim();
    $[0] = href;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const normalizedHref = t2;
  const hasHref = normalizedHref.length > 0;
  let t3;
  let t4;
  if ($[2] !== mode || $[3] !== normalizedHref) {
    t3 = [QueryKey.MARKETPLACE_PREVIEW, {
      href: normalizedHref,
      mode
    }];
    t4 = async () => {
      let preview = fromNftLink({
        href: normalizedHref
      });
      try {
        const nftLinkResponse = await fetchNftLink(normalizedHref);
        preview = fromNftLink({
          href: normalizedHref,
          response: nftLinkResponse
        });
      } catch {}
      if (!needsOpenGraphFallback(preview)) {
        return preview;
      }
      ;
      try {
        const linkPreview = await fetchLinkPreview(normalizedHref);
        return mergeOpenGraphFallback({
          href: normalizedHref,
          mode,
          current: preview,
          linkPreview
        });
      } catch (t5) {
        const error = t5;
        if (preview.media !== null) {
          return preview;
        }
        throw error;
      }
    };
    $[2] = mode;
    $[3] = normalizedHref;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== hasHref || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      enabled: hasHref,
      staleTime: MARKETPLACE_PREVIEW_STALE_TIME_MS,
      retry: false
    };
    $[6] = hasHref;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const marketplacePreviewQuery = useQuery(t5);
  if (!hasHref) {
    let t6;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = new Error("A valid URL is required to fetch link preview metadata.");
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] !== href) {
      t7 = {
        type: "error",
        href,
        error: t6
      };
      $[11] = href;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  if (marketplacePreviewQuery.isPending) {
    let t6;
    if ($[13] !== href) {
      t6 = {
        type: "loading",
        href
      };
      $[13] = href;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    return t6;
  }
  const resolvedMedia = marketplacePreviewQuery.data?.media;
  if (resolvedMedia !== null && resolvedMedia !== undefined) {
    const t6 = marketplacePreviewQuery.data?.price ?? undefined;
    const t7 = marketplacePreviewQuery.data?.priceCurrency ?? undefined;
    const t8 = marketplacePreviewQuery.data?.title;
    let t9;
    if ($[15] !== t8) {
      t9 = toNonEmptyString(t8);
      $[15] = t8;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] !== href || $[18] !== resolvedMedia || $[19] !== t6 || $[20] !== t7 || $[21] !== t9) {
      t10 = {
        type: "success",
        href,
        resolvedMedia,
        resolvedPrice: t6,
        resolvedPriceCurrency: t7,
        resolvedTitle: t9
      };
      $[17] = href;
      $[18] = resolvedMedia;
      $[19] = t6;
      $[20] = t7;
      $[21] = t9;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    return t10;
  }
  const error_0 = marketplacePreviewQuery.error;
  let t6;
  if ($[23] !== error_0) {
    t6 = toError(error_0, "Failed to load marketplace preview");
    $[23] = error_0;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== href || $[26] !== t6) {
    t7 = {
      type: "error",
      href,
      error: t6
    };
    $[25] = href;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
};